import { useEffect, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { Search, ChevronRight, ChevronLeft, Star, Folder, Plus, Cloud, Printer, FileText } from "lucide-react";

function TypingQuery({ text, active }) {
  const reduce = useReducedMotion();
  const [n, setN] = useState(reduce ? text.length : 0);
  useEffect(() => {
    if (reduce) {
      setN(text.length);
      return undefined;
    }
    if (!active) {
      setN(0);
      return undefined;
    }
    setN(0);
    const id = setInterval(() => {
      setN((v) => {
        if (v >= text.length) {
          clearInterval(id);
          return v;
        }
        return v + 1;
      });
    }, 110);
    return () => clearInterval(id);
  }, [active, text, reduce]);
  return (
    <span className="text-[15px] text-ink/70">
      {text.slice(0, n)}
      <span className="ml-[2px] inline-block h-4 w-[2px] animate-pulse bg-cobalt align-middle" />
    </span>
  );
}

export const StatusBar = () => (
  <div className="flex items-center justify-between px-7 pt-4 text-[15px] font-semibold text-ink">
    <span>9:41</span>
    <div className="flex items-center gap-1.5">
      <span className="flex items-end gap-[2.5px]" aria-hidden="true">
        {[4, 6, 9, 12].map((h) => (
          <i key={h} className="w-[4px] rounded-[1px] bg-ink" style={{ height: h }} />
        ))}
      </span>
      <span className="ml-1 inline-block h-[14px] w-[26px] rounded-[4px] border border-ink/50 p-[2px]" aria-hidden="true">
        <i className="block h-full w-3/4 rounded-[2px] bg-ink" />
      </span>
    </div>
  </div>
);

export const Chip = ({ children, accent = false }) => (
  <span
    className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${
      accent ? "border-cobalt/30 bg-cobalt/5 text-cobalt" : "border-ink/15 text-ink/50"
    }`}
  >
    {children}
  </span>
);

export function ScreenSearch({ appLabel, title, query, hint, chips = ["Billable", "2026"], rows, active = true }) {
  return (
    <div className="flex h-full flex-col bg-white">
      <StatusBar />
      <div className="px-5 pt-4">
        <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-ink/40">{appLabel}</p>
        <h3 className="mt-1.5 font-serif text-[30px] leading-none text-ink">{title}</h3>
        <div className="mt-4 flex items-center gap-2.5 rounded-2xl border border-ink/10 bg-paper px-4 py-3.5">
          <Search className="h-4 w-4 text-ink/35" />
          <TypingQuery text={query} active={active} />
        </div>
        {hint && <p className="mt-2 text-[11px] font-medium text-cobalt">Expanded: {hint}</p>}
        <div className="mt-3 flex gap-2">
          {chips.map((c, i) => (
            <Chip key={c} accent={i === 0}>{c}</Chip>
          ))}
        </div>
      </div>
      <div className="mt-4 flex-1">
        {rows.map(([code, label]) => (
          <div key={code} className="flex items-center justify-between border-b border-ink/5 px-5 py-3.5">
            <div>
              <p className="text-[17px] font-semibold tracking-tight text-ink">{code}</p>
              <p className="text-[13px] leading-tight text-ink/50">{label}</p>
            </div>
            <ChevronRight className="h-4 w-4 text-ink/25" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function ScreenDetail({ back = "Favorites", code, name, chips = ["Billable", "2026", "iCloud synced"], noteLabel, note, actions = ["Add to favorites", "Save to folder"] }) {
  const iconFor = (a) =>
    a.includes("Print") ? Printer : a.includes("report") ? FileText : a.toLowerCase().includes("folder") ? Folder : Star;
  return (
    <div className="flex h-full flex-col bg-white">
      <StatusBar />
      <div className="flex items-center gap-1.5 px-5 pt-4 text-[13px] font-semibold text-cobalt">
        <ChevronLeft className="h-4 w-4" /> {back}
      </div>
      <div className="px-5 pt-5">
        <p className="font-serif text-[46px] leading-none tracking-tight text-ink">{code}</p>
        <p className="mt-2.5 text-[16px] leading-snug text-ink/70">{name}</p>
        <div className="mt-3.5 flex flex-wrap gap-2">
          {chips.map((c, i) => (
            <Chip key={c} accent={i === 0}>{c}</Chip>
          ))}
        </div>
        {note && (
          <div className="mt-5 border-t border-ink/10 pt-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-ink/40">{noteLabel}</p>
            <p className="mt-2 text-[13px] leading-relaxed text-ink/60">{note}</p>
          </div>
        )}
      </div>
      <div className="mt-auto space-y-2.5 px-5 pb-6">
        {actions.map((a, i) => {
          const Icon = iconFor(a);
          return i === 0 ? (
            <div key={a} className="flex items-center justify-center gap-2 rounded-2xl bg-cobalt py-3.5 text-[14px] font-semibold text-white">
              <Icon className="h-4 w-4" /> {a}
            </div>
          ) : (
            <div key={a} className="flex items-center justify-center gap-2 rounded-2xl border border-ink/15 py-3.5 text-[14px] font-semibold text-ink">
              <Icon className="h-4 w-4" /> {a}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ScreenList({ appLabel, title, items, footer, dashed = "New folder" }) {
  return (
    <div className="flex h-full flex-col bg-white">
      <StatusBar />
      <div className="px-5 pt-4">
        <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-ink/40">{appLabel}</p>
        <h3 className="mt-1.5 font-serif text-[30px] leading-none text-ink">{title}</h3>
      </div>
      <div className="mt-5 flex-1">
        {items.map(([name, sub]) => (
          <div key={name} className="flex items-center justify-between border-b border-ink/5 px-5 py-4">
            <div className="flex items-center gap-3.5">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-cobalt/8">
                <Folder className="h-[18px] w-[18px] text-cobalt" />
              </span>
              <div>
                <p className="text-[16px] font-semibold tracking-tight text-ink">{name}</p>
                <p className="text-[12px] text-ink/50">{sub}</p>
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-ink/25" />
          </div>
        ))}
        {dashed && (
          <div className="flex items-center gap-3.5 px-5 py-4">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-dashed border-ink/25">
              <Plus className="h-[18px] w-[18px] text-ink/40" />
            </span>
            <p className="text-[15px] font-medium text-ink/40">{dashed}</p>
          </div>
        )}
      </div>
      {footer && (
        <div className="flex items-center justify-center gap-2 border-t border-ink/10 py-4 text-[12px] font-semibold text-ink/50">
          <Cloud className="h-4 w-4 text-cobalt" /> {footer}
        </div>
      )}
    </div>
  );
}
